/**
 * ============================================================
 * sounds.ts — Som de Notificacao via Web Audio API
 * ============================================================
 *
 * Responsabilidade: Gerar som de notificacao "plim" sintetizado
 *   em tempo real, sem precisar de arquivos de audio.
 *
 * Tecnica: 3 osciladores senoidais (fundamental 988Hz B5,
 *   harmonico 1480Hz, shimmer 2960Hz) com ataque rapido (~5ms)
 *   e decaimento exponencial (~250ms).
 *
 * Limitacao: Navegadores exigem interacao do usuario antes de
 *   permitir AudioContext. O contexto e resumido automaticamente
 *   se estiver suspenso.
 *
 * Quando usar: Em notificacoes real-time (novos leads, mensagens).
 * ============================================================
 */

let audioCtx: AudioContext | null = null;

function getAudioContext(): AudioContext | null {
  try {
    if (!audioCtx || audioCtx.state === 'closed') {
      audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
    }
    // Resume if suspended (browsers require user gesture to start audio)
    if (audioCtx.state === 'suspended') {
      audioCtx.resume().catch(() => {});
    }
    return audioCtx;
  } catch {
    return null;
  }
}

/**
 * Plays a short "plim" bell/chime notification sound.
 *
 * The sound is synthesised in real-time with the Web Audio API:
 * - A fundamental sine tone at 988 Hz (B5) for the main bell body
 * - A second harmonic at 1480 Hz for brightness
 * - A third, quieter harmonic at 2960 Hz for shimmer
 * - Quick attack (~5 ms) and fast exponential decay (~250 ms)
 *
 * @param volume - Master volume, 0 to 1. Default 0.3.
 */
export function playPlim(volume = 0.3): void {
  const ctx = getAudioContext();
  if (!ctx) return;

  const now = ctx.currentTime;
  const duration = 0.28; // total sound length in seconds

  const masterGain = ctx.createGain();
  masterGain.gain.setValueAtTime(volume, now);
  masterGain.connect(ctx.destination);

  // --- Fundamental tone (988 Hz – B5) ---
  const osc1 = ctx.createOscillator();
  const gain1 = ctx.createGain();
  osc1.type = 'sine';
  osc1.frequency.setValueAtTime(988, now);
  gain1.gain.setValueAtTime(0, now);
  gain1.gain.linearRampToValueAtTime(0.6, now + 0.005); // fast attack
  gain1.gain.exponentialRampToValueAtTime(0.001, now + duration);
  osc1.connect(gain1);
  gain1.connect(masterGain);

  // --- Second harmonic (1480 Hz) for brightness ---
  const osc2 = ctx.createOscillator();
  const gain2 = ctx.createGain();
  osc2.type = 'sine';
  osc2.frequency.setValueAtTime(1480, now);
  gain2.gain.setValueAtTime(0, now);
  gain2.gain.linearRampToValueAtTime(0.3, now + 0.005);
  gain2.gain.exponentialRampToValueAtTime(0.001, now + duration * 0.8);
  osc2.connect(gain2);
  gain2.connect(masterGain);

  // --- Third harmonic (2960 Hz) for shimmer ---
  const osc3 = ctx.createOscillator();
  const gain3 = ctx.createGain();
  osc3.type = 'sine';
  osc3.frequency.setValueAtTime(2960, now);
  gain3.gain.setValueAtTime(0, now);
  gain3.gain.linearRampToValueAtTime(0.12, now + 0.003);
  gain3.gain.exponentialRampToValueAtTime(0.001, now + duration * 0.5);
  osc3.connect(gain3);
  gain3.connect(masterGain);

  // Start and schedule stop for all oscillators
  osc1.start(now);
  osc2.start(now);
  osc3.start(now);
  osc1.stop(now + duration);
  osc2.stop(now + duration);
  osc3.stop(now + duration);

  // Clean up nodes after sound finishes
  setTimeout(() => {
    try {
      osc1.disconnect();
      osc2.disconnect();
      osc3.disconnect();
      gain1.disconnect();
      gain2.disconnect();
      gain3.disconnect();
      masterGain.disconnect();
    } catch {
      // Already disconnected – ignore
    }
  }, (duration + 0.1) * 1000);
}
